import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ApiError,
  logout as apiLogout,
  type CurrentUser,
  clearStoredCsrfToken,
  currentUser,
  ensureSessionCsrfToken,
} from "@/lib/api";
import { showToast, ToastHost } from "@sylviq/ui-react-web/components/ui/toast";

type SessionState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; user: CurrentUser };

interface SessionValue {
  state: SessionState;
  /** Re-checks `/user` — call after signup/login to pick up the new session. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: "loading" });
  const operation = useRef(0);
  const logoutPending = useRef(false);

  const refresh = useCallback(async () => {
    if (logoutPending.current) return;
    const sequence = ++operation.current;
    try {
      const user = await currentUser();
      if (sequence !== operation.current) return;
      try {
        await ensureSessionCsrfToken();
      } catch (cause) {
        if (sequence !== operation.current) return;
        if (cause instanceof ApiError && cause.status === 401) {
          clearStoredCsrfToken();
          setState({ status: "anonymous" });
          return;
        }
        showToast("Session security token could not be restored. Protected actions remain blocked; check your connection and refresh.", "error");
      }
      if (sequence !== operation.current) return;
      setState({ status: "authenticated", user });
    } catch (cause) {
      if (sequence !== operation.current) return;
      if (cause instanceof ApiError && cause.status === 401) {
        setState({ status: "anonymous" });
      } else {
        // Network/server error: don't strand the visitor on an infinite
        // loading screen — treat as signed-out and let them retry from
        // the login page, same as an expired session would look.
        setState({ status: "anonymous" });
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    if (logoutPending.current) return;
    logoutPending.current = true;
    ++operation.current;
    try {
      await apiLogout();
      ++operation.current;
      clearStoredCsrfToken();
      // The login hint in `@/lib/remember` is intentionally kept on logout —
      // only an explicit Forget clears it, so the next sign-in can still
      // offer the remembered account. There is no expiry on the hint.
      setState({ status: "anonymous" });
    } catch {
      showToast("Sign-out was not confirmed. You may still be signed in. Check your connection and try signing out again.", "error");
    } finally {
      logoutPending.current = false;
    }
  }, []);

  return (
    <SessionContext.Provider value={{ state, refresh, signOut }}>
      <ToastHost />
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value)
    throw new Error("useSession() must be used inside SessionProvider");
  return value;
}
